import { useHasCart } from '@/feature/cart/hooks/useHasCart'
import { REFERENCES_PRODUCT_ID } from '@/feature/product/services/product.constants'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Icon } from '@/ui/icons/icon/Icon'
import type { RefObject } from 'react'
import type { MergedProduct } from '../../types/mergedProduct'
import type { ProductAvailabilityIssueCardProps } from '../product-availability-issue-card/ProductAvailabilityIssueCard.client'
import { ProductPriceLoginLink } from '../product-price-login-link/ProductPriceLoginLink'
import { ProductBuyBoxAddToCart } from './ProductBuyBoxAddToCart'

import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { Link } from '@/ui/links/link/Link.client'

type ProductBuyBoxCTAProps = {
  cartId?: string
  isProduct: boolean
  isMonoDiscriminant: boolean
  hasPrice: boolean
  nbArticles: number
  availabilityIssue?: ProductAvailabilityIssueCardProps['issue']
  stickyPositionClasses: string
  product: MergedProduct
  filterGroupRef: RefObject<HTMLDivElement | null>
  favoritePOSPhoneNumber?: string
  isLoading?: boolean
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function ProductBuyBoxCTA({
  cartId,
  isProduct,
  isMonoDiscriminant,
  hasPrice,
  nbArticles,
  availabilityIssue,
  stickyPositionClasses,
  product,
  filterGroupRef,
  favoritePOSPhoneNumber,
  isLoading,
}: ProductBuyBoxCTAProps) {
  const { t } = useTranslation(['productBuyBox'])
  const hasCart = useHasCart(cartId)
  const isMultiDiscriminantProduct = isProduct && !isMonoDiscriminant
  const shouldShowGuestLoginLink = !hasCart && !isMultiDiscriminantProduct && !availabilityIssue

  const renderStickyLoginLink = () => (
    <div className={stickyPositionClasses}>
      <ProductPriceLoginLink mode="detailed" origin="product-buybox" />
    </div>
  )

  // Product Mono Discriminant (families with single filter, e.g. some cases in 4, 5, 6…) — @see https://descourscabaudnet.sharepoint.com/:x:/r/sites/DigitalFactory/_layouts/15/doc.aspx?sourcedoc=%7B7b7c714a-58b2-4228-a022-953996989352%7D
  if (isProduct && isMonoDiscriminant) {
    // Show login link for guests even with an availabilityIssue (same fix as Article path)
    if (!hasCart) return <ProductPriceLoginLink mode="detailed" origin="product-buybox" />

    if (!hasPrice && !isLoading) return null

    return (
      <ProductBuyBoxAddToCart
        product={product}
        cartId={cartId}
        isMonoDiscriminant={isMonoDiscriminant}
        filterGroupRef={filterGroupRef}
        hasPrice={hasPrice}
        favoritePOSPhoneNumber={favoritePOSPhoneNumber}
        isLoading={isLoading}
      />
    )
  }

  // Product Multi Discriminants
  if (isMultiDiscriminantProduct) {
    return (
      <Link
        bold
        href={`#${REFERENCES_PRODUCT_ID}`}
        className="w-full"
        variant="button-primary"
        iconLeft={<Icon.ShoppingBasket />}
        onPress={() => {
          pushToGtmDataLayer({
            event: 'gaEvent',
            eventCategory: 'fiche produit multi-discriminant',
            eventAction: 'cta choisir parmi les références',
            eventLabel: 'accès tableau de références',
          })
        }}
        intent={INTENT_SAME_AS_WEB}
      >
        {t('productBuyBox:cta.references', { count: nbArticles })}
      </Link>
    )
  }

  // Article with availability issue (cases 1_10: isNotSellable, 2_10: noPricesAvailable, 3_10/3_20: storeOnly) — @see https://descourscabaudnet.sharepoint.com/:x:/r/sites/DigitalFactory/_layouts/15/doc.aspx?sourcedoc=%7B7b7c714a-58b2-4228-a022-953996989352%7D
  // Show login link for guests (via cartId, since useIsAuthenticated is undefined during SSR) even with an availabilityIssue, to avoid an empty buybox on non-public-price sites.
  if (availabilityIssue) {
    if (!hasCart) return renderStickyLoginLink()

    return (
      <div className="max-w-full">
        <ProductBuyBoxAddToCart
          product={product}
          cartId={cartId}
          isMonoDiscriminant={isMonoDiscriminant}
          filterGroupRef={filterGroupRef}
          availabilityIssue={availabilityIssue}
          hasPrice={hasPrice}
          favoritePOSPhoneNumber={favoritePOSPhoneNumber}
          isLoading={isLoading}
        />
      </div>
    )
  }

  // Article without issue, guest user (disconnected) — cases 4+ when not authenticated
  if (shouldShowGuestLoginLink) {
    return renderStickyLoginLink()
  }

  // Article without prices showed when not signedIn
  if (!hasPrice && !isLoading)
    return <ProductPriceLoginLink mode="detailed" origin="product-buybox" />

  // Article with price/availability issue, connected — cases 4+ with canAddToCart=true (add to cart) or canAddToCart=false (nothing/equivalent products)
  if (hasCart) {
    return (
      <div className="max-w-full">
        <ProductBuyBoxAddToCart
          product={product}
          cartId={cartId}
          isMonoDiscriminant={isMonoDiscriminant}
          filterGroupRef={filterGroupRef}
          availabilityIssue={availabilityIssue ?? undefined}
          hasPrice={hasPrice}
          favoritePOSPhoneNumber={favoritePOSPhoneNumber}
          isLoading={isLoading}
        />
      </div>
    )
  }

  return renderStickyLoginLink()
}
